/** Reason a candidate file was refused by `validateFiles`. */
type RejectReason = 'too-large' | 'too-many' | 'too-big-total' | 'wrong-type' | 'duplicate';

/** A refused candidate together with the (first) reason it was refused. */
type RejectedFile = { file: File; reason: RejectReason };

/** Constraint set for `validateFiles`; mirrors the `NxFileInput` props. */
type FileInputConstraints = {
  /** `false` → the selection replaces the current file and is capped at one entry. */
  multiple: boolean;
  /** Native-style accept list: exact MIME (`image/png`), MIME wildcard (`image/*`), extension (`.pdf`). */
  accept?: string;
  /** Maximum number of files (only meaningful when `multiple`). */
  maxFiles?: number;
  /** Maximum size in bytes per file. */
  maxSize?: number;
  /** Maximum size in bytes across current + newly accepted files. */
  maxTotalSize?: number;
  /** Refuse files already present (same name + size + lastModified). */
  dedupe: boolean;
};

type ValidationResult = { accepted: File[]; rejected: RejectedFile[] };

/** `true` when the file matches the accept list (or no list is given). */
function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;
  const patterns = accept
    .split(',')
    .map((pattern) => pattern.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  const mime = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) return name.endsWith(pattern);
    // Browsers report an empty `file.type` for many drag-dropped files (esp. on
    // Windows). Don't reject on a MIME/wildcard pattern we can't evaluate —
    // extension patterns above stay authoritative when the accept list has them.
    if (pattern.endsWith('/*')) return mime === '' || mime.startsWith(pattern.slice(0, -1));
    return mime === '' || mime === pattern;
  });
}

/** Identity check used by `dedupe`: name + size + lastModified. */
function isSameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

/**
 * Splits candidate files into accepted and rejected against the current
 * selection and the given constraints. Never throws; candidates are processed
 * in input order and each is checked in the fixed order
 * wrong-type → too-large → duplicate → too-many → too-big-total (first failure wins).
 *
 * In single mode (`multiple: false`) the selection replaces `current`, so the
 * current files are ignored and the count is capped at one.
 */
function validateFiles(
  candidates: readonly File[],
  current: readonly File[],
  constraints: FileInputConstraints
): ValidationResult {
  const { multiple, accept, maxFiles, maxSize, maxTotalSize, dedupe } = constraints;
  const kept = multiple ? current : [];
  const countLimit = multiple ? maxFiles : 1;
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  let totalSize = kept.reduce((sum, file) => sum + file.size, 0);

  for (const file of candidates) {
    if (!matchesAccept(file, accept)) {
      rejected.push({ file, reason: 'wrong-type' });
    } else if (maxSize !== undefined && file.size > maxSize) {
      rejected.push({ file, reason: 'too-large' });
    } else if (dedupe && [...kept, ...accepted].some((existing) => isSameFile(existing, file))) {
      rejected.push({ file, reason: 'duplicate' });
    } else if (countLimit !== undefined && kept.length + accepted.length >= countLimit) {
      rejected.push({ file, reason: 'too-many' });
    } else if (maxTotalSize !== undefined && totalSize + file.size > maxTotalSize) {
      rejected.push({ file, reason: 'too-big-total' });
    } else {
      accepted.push(file);
      totalSize += file.size;
    }
  }

  return { accepted, rejected };
}

/** Human-readable file size: `512 B`, `1.5 KB`, `10 MB`, … (base 1024, one decimal below 10). */
function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const rounded =
    unitIndex === 0 ? String(value) : Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
}

export { formatFileSize, validateFiles };
export type { FileInputConstraints, RejectReason, RejectedFile, ValidationResult };
